import { Button } from "@/components/ui/button";
import { Shield, Zap } from "lucide-react";
import { Product } from "@/lib/products";
import cynthiaImg from "@/assets/cynthia-simple.webp";
import packDuoAsset from "@/assets/pack-duo.mp4.asset.json";

interface ProductHeroProps {
  product: Product;
  onBuyNow: () => void;
  formatPrice: (n: number) => string;
}

const heroContent: Record<string, { h1: React.ReactNode; subtitle: string }> = {
  cynthia: {
    h1: (
      <>
        Ne noyez plus vos plantes. <span className="text-primary">Laissez CynthIA s'en occuper.</span>
      </>
    ),
    subtitle: "L'expert horticole dans votre poche.\nPour un jardin parfait, sans effort.",
  },
  "pack-duo": {
    h1: (
      <>
        Doublez la protection. <span className="text-primary">Couvrez tout votre jardin.</span>
      </>
    ),
    subtitle: "2 boîtiers, 2 zones, 1 seule app.\nL'offre ultime pour ne plus jamais stresser.",
  },
};

export const ProductHero = ({ product, onBuyNow, formatPrice }: ProductHeroProps) => {
  const isDuo = product.id === "pack-duo";
  const content = heroContent[product.id] ?? heroContent.cynthia;

  return (
    <section className="px-4 pt-6 pb-8 md:pt-10 md:pb-14">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Text — mobile first */}
          <div className="order-2 md:order-1 space-y-5 animate-[fadeInUp_0.4s_ease-out_both]">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight text-foreground">
              {content.h1}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed whitespace-pre-line">
              {content.subtitle}
            </p>

            {/* Price */}
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="text-4xl font-extrabold text-primary">{formatPrice(product.price)}</span>
              {isDuo && <span className="text-sm line-through text-muted-foreground/60">{formatPrice(89 * 2)}</span>}
              <span className="text-sm text-muted-foreground font-medium bg-secondary px-3 py-1 rounded-full">
                Paiement unique · 0 abonnement
              </span>
            </div>

            {isDuo && (
              <p className="text-sm font-semibold text-primary">
                🔥 Économisez {formatPrice(89 * 2 - product.price)} par rapport à 2 packs Solo
              </p>
            )}

            {/* CTA */}
            <Button
              size="lg"
              onClick={onBuyNow}
              disabled={!product.available}
              className="w-full md:w-auto text-base font-bold px-8 py-6 rounded-xl bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg hover:shadow-xl transition-all"
            >
              <Zap className="w-5 h-5 mr-2" />
              Profiter de l'offre Fondateur
            </Button>

            {/* Mini trust */}
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground pt-1">
              <span className="flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-primary" />
                Satisfait ou remboursé 30j
              </span>
              <span>🇫🇷 Conçu en France</span>
              <span>🔒 Paiement sécurisé</span>
            </div>
          </div>

          {/* Visual */}
          <div className="order-1 md:order-2 animate-[fadeIn_0.5s_ease-out_0.1s_both]">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-secondary/30 border border-border/30 shadow-lg">
              {isDuo ? (
                <video src={packDuoAsset.url} autoPlay loop muted playsInline preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <img
                  src={cynthiaImg}
                  alt="CynthIA — boîtier intelligent planté dans la terre avec application mobile"
                  className="w-full h-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="sync"
                  width={800}
                  height={600}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
